import { z } from 'zod';
import { BUILDING_TYPE_KEYS, BUILDING_TYPES, type BuildingTypeKey } from '../patterns/patterns.ts';
import type { NodeKind } from './schema.ts';

/**
 * A step's special tasks (spec "Pattern Mapping" §3; Jason, 2026-10-15: "we should rework how special tasks are linked
 * to nodes. Maybe like in customizing or setting up a node has a general tool for selecting from among a set of
 * available custom tasks?"): one library, each entry what a step carrying it does on a job, the kinds of step that may
 * carry it, what is set for it at project setup and what finishes it. A step of the master lists the ones it carries
 * (`ProcessNodeDef.special`), chosen on its card on the process tree page; a job reads them from the tree it reads,
 * every time, as it reads whether a step delivers an estimate.
 *
 * Pattern mapping is the first, and for now the only one (Jason, 2026-10-17: "we can leave the switch for needs review.
 * I'm not so sure about the estimate yet, because the estimate might be a special tool itself"): the design review,
 * the estimate and the permit, inspection and pre-install holds keep their switches.
 */

export const SPECIAL_KEYS = ['patterns'] as const;
export type SpecialKey = (typeof SPECIAL_KEYS)[number];

export interface SpecialTask {
  key: SpecialKey;
  /** Its name, as the card and a step's panel write it. */
  name: string;
  /** What a step carrying it does on a job, in a sentence. */
  does: string;
  /** The kinds of step that may carry it. */
  kinds: readonly NodeKind[];
  /** What is set for it on a job before the step is configured, and where; null when nothing is. */
  setAtSetup: string | null;
  /** What its settings on a job are called, as a heading and a line of the job's draft write them; null when it has none. */
  settingsName: string | null;
  /** What finishes it, read after "Done when:". */
  doneWhen: string;
}

export const SPECIAL_TASKS: Record<SpecialKey, SpecialTask> = {
  patterns: {
    key: 'patterns',
    name: 'Pattern mapping',
    does: 'Opens the job’s Patterns page: NSB and the client pick, from every pattern its buildings could touch, the ones this house should follow, and how much each matters.',
    kinds: ['work'],
    setAtSetup: 'What is being built, ticked when the step is added at project setup',
    settingsName: 'What is being built',
    doneWhen: 'the client confirms the language, which locks it',
  },
};

/** The library's special tasks a kind of step may carry, in the library's order. */
export function specialsFor(kind: NodeKind): SpecialTask[] {
  return SPECIAL_KEYS.map((k) => SPECIAL_TASKS[k]).filter((s) => s.kinds.includes(kind));
}

/** The special tasks a node carries: none when it says nothing. */
export function specialsOf(n: { special?: readonly SpecialKey[] | undefined }): SpecialKey[] {
  return n.special ? [...n.special] : [];
}

/**
 * What a job's step carrying a special task has set for it (spec §4.2): for Pattern mapping, what is being built, any
 * of the building types, ticked at project setup.
 */
export interface SpecialSettings {
  patterns: { types: BuildingTypeKey[] };
}

/** Reads a special task's settings as sent: the building types without repeats, in their own order. */
export const SpecialSettingsSchema: { [K in SpecialKey]: z.ZodType<SpecialSettings[K]> } = {
  patterns: z
    .object({
      types: z
        .array(z.enum(BUILDING_TYPE_KEYS))
        .max(BUILDING_TYPE_KEYS.length)
        .refine((t) => new Set(t).size === t.length, 'A building type is ticked once.'),
    })
    .transform((s) => ({ types: BUILDING_TYPE_KEYS.filter((k) => s.types.includes(k)) })),
};

/** Whether a job's step has what its special task needs set (spec §3.3): until it does, the step is Not configured. */
export function specialSet<K extends SpecialKey>(key: K, settings: SpecialSettings[K] | null): boolean {
  switch (key) {
    case 'patterns':
      return Boolean(settings && settings.types.length > 0);
  }
  return false;
}

/** A special task's settings in words, as a sentence carries them: for Pattern mapping, what is being built. */
export function specialWords<K extends SpecialKey>(key: K, settings: SpecialSettings[K] | null): string {
  switch (key) {
    case 'patterns': {
      const names = (settings?.types ?? []).map((t) => BUILDING_TYPES[t].name.replace(/^A /, 'a '));
      if (!names.length) return 'nothing ticked yet';
      return names.length === 1 ? names[0]! : `${names.slice(0, -1).join(', ')} and ${names.at(-1)!}`;
    }
  }
  return '';
}

/**
 * Whether a job's step carries a special task: its node in the job's tree lists it, and it is the step itself at the
 * top. A named copy is a second of the same work and carries none, so a job's step has one language, never a second.
 */
export function carriesSpecial(node: { special?: readonly SpecialKey[] | undefined } | undefined, s: { instance: string | null; parentId: string | null }, key: SpecialKey): boolean {
  return Boolean(node && specialsOf(node).includes(key) && s.instance === null && s.parentId === null);
}
